/**
 * pendingStayId localStorage 관리 유틸리티
 * 
 * pendingStayId는 /stays/:id URL로 진입 시 추출된 stayId를
 * localStorage에 임시 저장하여, 온보딩 완료 후 또는 즉시 stay 정보를 로드하는 데 사용됩니다.
 * 
 * 이 유틸리티는 모든 pendingStayId 접근을 중앙화하여:
 * - localStorage 실패 시 안전한 처리
 * - 만료 시간 관리
 * - 일관된 에러 핸들링
 * 을 제공합니다.
 */

const PENDING_STAY_ID_KEY = 'pendingStayId';
const PENDING_STAY_TIMESTAMP_KEY = 'pendingStayTimestamp';

/** pendingStayId 유효 기간: 24시간 */
const EXPIRY_MS = 24 * 60 * 60 * 1000;

/**
 * pendingStayId를 localStorage에 저장합니다.
 * @returns 저장 성공 여부
 */
export const savePendingStayId = (stayId: string): boolean => {
  try {
    localStorage.setItem(PENDING_STAY_ID_KEY, stayId);
    localStorage.setItem(PENDING_STAY_TIMESTAMP_KEY, String(Date.now()));
    return true;
  } catch (error) {
    console.error('[pendingStayUtils] Failed to save pendingStayId:', error);
    return false;
  }
};

/**
 * localStorage에서 pendingStayId를 읽어옵니다.
 * 만료된 경우 자동으로 제거하고 null을 반환합니다.
 */
export const loadPendingStayId = (): string | null => {
  try {
    const stayId = localStorage.getItem(PENDING_STAY_ID_KEY);
    if (!stayId) return null;

    // 만료 확인
    const timestamp = localStorage.getItem(PENDING_STAY_TIMESTAMP_KEY);
    if (timestamp) {
      const elapsed = Date.now() - Number(timestamp);
      if (elapsed > EXPIRY_MS) {
        // 만료 — 정리 후 null 반환
        removePendingStayId();
        return null;
      }
    }

    return stayId;
  } catch (error) {
    console.error('[pendingStayUtils] Failed to load pendingStayId:', error);
    return null;
  }
};

/**
 * localStorage에서 pendingStayId를 제거합니다.
 */
export const removePendingStayId = (): void => {
  try {
    localStorage.removeItem(PENDING_STAY_ID_KEY);
    localStorage.removeItem(PENDING_STAY_TIMESTAMP_KEY);
  } catch (error) {
    console.error('[pendingStayUtils] Failed to remove pendingStayId:', error);
  }
};
